/**
 * 分析工作台页面
 *
 * 提供多维度分析能力，用户可灵活选择维度、筛选条件和可视化类型
 * 支持下钻上卷、保存分析配置等高级功能
 */

import { useState, useMemo } from 'react';
import { Card, Button, Space, App, Spin } from 'antd';
import {
  DownloadOutlined,
  SaveOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import {
  ChartType,
  DimensionType,
  FilterValues,
  DrillHistory,
} from '../../types/analysis';
import {
  MOCK_FEEDBACK_DATA,
  generateAggregatedData,
  generateTimeSeriesData,
  generateMosaicData,
  generateWordCloudData,
  generateScatterData,
} from '../../utils/mockData';
import { LineChart } from '../../components/organisms/charts/LineChart/LineChart';
import { BarChart } from '../../components/organisms/charts/BarChart/BarChart';
import { MosaicChart } from '../../components/organisms/charts/MosaicChart/MosaicChart';
import { PieChart } from '../../components/organisms/charts/PieChart/PieChart';
import { WordCloudChart } from '../../components/organisms/charts/WordCloudChart/WordCloudChart';
import { ScatterChart } from '../../components/organisms/charts/ScatterChart/ScatterChart';
import './AnalysisWorkbench.css';

export function AnalysisWorkbench() {
  const { message } = App.useApp();
  // 状态管理
  const [selectedDimensions, setSelectedDimensions] = useState<DimensionType[]>(['productCategory']);
  const [filters] = useState<FilterValues>({});
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [drillHistory, setDrillHistory] = useState<DrillHistory>({
    levels: [],
    canDrillUp: false,
    canDrillDown: true,
  });
  const [loading, setLoading] = useState(false);

  // 维度选项
  const dimensionOptions = [
    { value: 'productCategory' as DimensionType, label: '产品品类' },
    { value: 'productBrand' as DimensionType, label: '产品品牌' },
    { value: 'productModel' as DimensionType, label: '产品型号' },
    { value: 'channel' as DimensionType, label: '渠道来源' },
    { value: 'sentiment' as DimensionType, label: '情感维度' },
    { value: 'experienceType' as DimensionType, label: '体验类型' },
  ];

  // 图表类型选项
  const chartTypeOptions = [
    { value: 'line' as ChartType, label: '折线图', icon: '📈' },
    { value: 'bar' as ChartType, label: '柱状图', icon: '📊' },
    { value: 'pie' as ChartType, label: '饼图', icon: '🥧' },
    { value: 'mosaic' as ChartType, label: '马赛克图', icon: '🎨' },
    { value: 'wordCloud' as ChartType, label: '词云图', icon: '☁️' },
    { value: 'scatter' as ChartType, label: '散点图', icon: '⚫' },
  ];

  // 筛选数据
  const filteredData = useMemo(() => {
    return MOCK_FEEDBACK_DATA.filter((item) => {
      // 应用筛选条件
      if (filters.productCategory && !filters.productCategory.includes(item.productCategory)) {
        return false;
      }
      if (filters.sentiment && !filters.sentiment.includes(item.sentiment)) {
        return false;
      }
      return true;
    });
  }, [filters]);

  // 生成图表数据
  const chartData = useMemo(() => {
    const dimension = selectedDimensions[0] || 'productCategory';

    switch (chartType) {
      case 'line':
        return generateTimeSeriesData(filteredData);
      case 'bar':
      case 'pie':
        return generateAggregatedData(filteredData, dimension);
      case 'mosaic':
        return generateMosaicData(filteredData);
      case 'wordCloud':
        return generateWordCloudData(filteredData);
      case 'scatter':
        return generateScatterData(filteredData);
      default:
        return [];
    }
  }, [filteredData, chartType, selectedDimensions]);

  // 维度选择处理
  const handleDimensionChange = (dimension: DimensionType) => {
    if (selectedDimensions.includes(dimension)) {
      setSelectedDimensions(selectedDimensions.filter((d) => d !== dimension));
    } else {
      if (selectedDimensions.length < 3) {
        setSelectedDimensions([...selectedDimensions, dimension]);
      } else {
        message.warning('最多选择 3 个维度');
      }
    }
  };

  // 图表类型切换
  const handleChartTypeChange = (type: ChartType) => {
    setChartType(type);
  };

  // 刷新数据
  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      message.success('数据已刷新');
    }, 1000);
  };

  // 导出数据
  const handleExport = () => {
    message.success('导出功能开发中...');
  };

  // 保存分析配置
  const handleSave = () => {
    // TODO: 实现保存配置到后端的逻辑
    message.success('分析配置已保存');
  };

  // 上卷
  const handleDrillUp = () => {
    if (drillHistory.levels.length === 0) return;

    const newLevels = drillHistory.levels.slice(0, -1);
    setDrillHistory({
      levels: newLevels,
      canDrillUp: newLevels.length > 0,
      canDrillDown: true,
    });

    // 恢复上一层维度
    if (newLevels.length === 0) {
      setSelectedDimensions(['productCategory']);
    } else {
      const lastLevel = newLevels[newLevels.length - 1];
      setSelectedDimensions([lastLevel.dimension]);
    }

    message.info('已返回上一层');
  };

  // 渲染图表
  const renderChart = () => {
    if (loading) {
      return (
        <div className="chart-loading">
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#666' }}>加载中...</div>
        </div>
      );
    }

    switch (chartType) {
      case 'line':
        return <LineChart data={chartData as any} title="时间趋势分析" />;
      case 'bar':
        return <BarChart data={chartData as any} title="分类数据对比" />;
      case 'pie':
        return <PieChart data={chartData as any} title="占比分布分析" />;
      case 'mosaic':
        return <MosaicChart data={chartData as any} />;
      case 'wordCloud':
        return <WordCloudChart data={chartData as any} title="关键词词云" />;
      case 'scatter':
        return <ScatterChart data={chartData as any} title="满意度-提及次数散点图" xAxisName="满意度" yAxisName="提及次数" />;
      default:
        return (
          <div className="chart-placeholder">
            <div className="placeholder-text">
              图表类型不支持
            </div>
          </div>
        );
    }
  };

  return (
    <div className="analysis-workbench">
      {/* 控制面板 */}
      <Card className="control-panel">
        {/* 维度选择 */}
        <div className="control-section">
          <h3 className="section-title">选择分析维度 (最多3个)</h3>
          <div className="dimension-buttons">
            {dimensionOptions.map((option) => (
              <Button
                key={option.value}
                type={selectedDimensions.includes(option.value) ? 'primary' : 'default'}
                onClick={() => handleDimensionChange(option.value)}
                className="dimension-button"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* 图表类型选择 */}
        <div className="control-section">
          <h3 className="section-title">选择图表类型</h3>
          <div className="chart-type-buttons">
            {chartTypeOptions.map((option) => (
              <Button
                key={option.value}
                type={chartType === option.value ? 'primary' : 'default'}
                onClick={() => handleChartTypeChange(option.value)}
                className="chart-type-button"
              >
                <span className="button-icon">{option.icon}</span>
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="control-section">
          <Space size="middle">
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              刷新数据
            </Button>
            <Button icon={<SaveOutlined />} onClick={handleSave}>
              保存分析
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出数据
            </Button>
            {drillHistory.canDrillUp && (
              <Button icon={<ArrowUpOutlined />} onClick={handleDrillUp}>
                上卷
              </Button>
            )}
          </Space>
        </div>

        {/* 下钻路径显示 */}
        {drillHistory.levels.length > 0 && (
          <div className="drill-path">
            <span className="path-label">当前路径:</span>
            {drillHistory.levels.map((level, index) => (
              <span key={index} className="path-item">
                {level.value}
                {index < drillHistory.levels.length - 1 && <span className="path-separator"> &gt; </span>}
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* 图表展示区 */}
      <Card className="chart-container">
        {renderChart()}
      </Card>

      {/* 数据统计 */}
      <Card className="data-summary">
        <div className="summary-item">
          <span className="summary-label">数据总数:</span>
          <span className="summary-value">{filteredData.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">平均满意度:</span>
          <span className="summary-value">
            {(filteredData.reduce((sum, item) => sum + item.satisfactionScore, 0) / filteredData.length).toFixed(1)} 分
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">正面率:</span>
          <span className="summary-value">
            {((filteredData.filter((item) => item.sentiment === 'positive').length / filteredData.length) * 100).toFixed(
              1
            )}
            %
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">负面率:</span>
          <span className="summary-value">
            {((filteredData.filter((item) => item.sentiment === 'negative').length / filteredData.length) * 100).toFixed(
              1
            )}
            %
          </span>
        </div>
      </Card>

    </div>
  );
}
